import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../services/axios-instance";
import { ORGANIZATION_MONTHLY_LOGS_URL } from "../../../services/endpoints";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import TimelogTable from './TimelogTable';
import { TimelogsData } from '../../../interface/timelog';
import { exportOrganizationTimelogToExcel } from '../../../utils/exportTimelog';

const SummaryHeaderCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.table.header,
    color: theme.palette.table.headerText,
    padding: "12px 16px",
    fontSize: 16,
  },
}));

const GrandTotalCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: theme.palette.table.footer,
    color: theme.palette.table.footerText,
    fontSize: 18,
    fontWeight: 700,
    padding: "12px 16px",
  },
}));

const OrganizationTimelogReport = () => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const [timelogsData, setTimelogsData] = useState<TimelogsData>({});

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const prevYear = currentYear - 1;
  const yearsArray = [prevYear, currentYear, nextYear];

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [totalHoursMonthly, setTotalHoursMonthly] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [clientNames, setClientNames] = useState<string[]>([]);
  const { id } = useParams();
  const organizationId = id ?? -1;

  const getUserData = useCallback((key: number) => {
    const keyStr = key.toString();
    return timelogsData[keyStr];
  }, [timelogsData]);

  const getKeyData = useCallback((key: number) => {
    const userData = getUserData(key);
    if (!userData?.data) return [];
    return userData.data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [getUserData]);

  const getTotalHours = useCallback((key: number) => {
    return getUserData(key)?.total_hours ? (+getUserData(key).total_hours / 60).toFixed(1) : "0.0";
  }, [getUserData]);

  const getUser = useCallback((key: number) => {
    return getUserData(key)?.username ?? "";
  }, [getUserData]);

  useEffect(() => {
      async function fetchData() {
          try {
              setTimelogsData({});

              const response = await axiosInstance.get(ORGANIZATION_MONTHLY_LOGS_URL(+organizationId, selectedMonth, selectedYear));
              const data = response.data;

              if (data.organization_name) {
                  setOrganizationName(data.organization_name);
              }
              if (data.clients) {
                  setClientNames(data.clients);
              }

              setTimelogsData(data.logs || {});
          } catch (error) {
              console.error("Error fetching logs:", error);
          }
      }
    fetchData();
  },[organizationId, selectedMonth, selectedYear]);

  useEffect(() => {
    function calculateHoursMonthly() {
      let totalSum = 0;
      Object.values(timelogsData).forEach(item => {
        totalSum += parseFloat((+item['total_hours']/60).toString());
      });
      setTotalHoursMonthly(totalSum.toFixed(2));
    }
    calculateHoursMonthly();
  },[timelogsData]);

    return (
      <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', marginBottom: 2 }}>
        <FormControl>
          <InputLabel id="select-month">Month</InputLabel>
          <Select
              name="month"
              value={selectedMonth-1}
              label="Month"
              labelId="select-month"
              id="select-month"
              onChange={(e)=> {setSelectedMonth(+e.target.value + 1)}}
            >
              {monthNames.map((month,i) => (
                <MenuItem key={i} value={i}>
                  {month}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl>
          <InputLabel id="select-year">Year</InputLabel>
          <Select
              name="year"
              value={selectedYear}
              label="Year"
              labelId="select-year"
              id="select-year"
              onChange={(e)=> {setSelectedYear(+e.target.value)}}
            >
              {yearsArray.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={() => exportOrganizationTimelogToExcel(+organizationId, selectedMonth, selectedYear)}
          disabled={Object.keys(timelogsData).length === 0}
        >
          Export Excel
        </Button>
      </Box>
      <h3>Logged a total of {totalHoursMonthly} hours for {organizationName} this month</h3>
      {clientNames.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ marginTop: "0.25rem" }}>
          Clients: {clientNames.join(", ")}
        </Typography>
      )}
      <br></br>
      {Object.keys(timelogsData).map((key) => {
          try {
              const data = getKeyData(+key);
              const user = getUser(+key);
              const totalHours = getTotalHours(+key);

              return (
                <TimelogTable key={key} data={data} user={user} totalHours={totalHours}/>
              );
          } catch (error) {
              console.error(`Error processing key ${key}:`, error);
              return null;
          }
      })}
      {Object.keys(timelogsData).length == 0 ? <div>No Logs Found For This Month</div> : (
        <TableContainer component={Paper} sx={{ mt: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <SummaryHeaderCell>User</SummaryHeaderCell>
                <SummaryHeaderCell align="right">Total Hours</SummaryHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(timelogsData).map((key, index) => (
                <TableRow key={key} sx={{ backgroundColor: index % 2 === 0 ? "table.rowOdd" : "table.rowEven" }}>
                  <TableCell sx={{ padding: "12px 16px", fontSize: 16 }}>{getUser(+key)}</TableCell>
                  <TableCell align="right" sx={{ padding: "12px 16px", fontSize: 16 }}>{getTotalHours(+key)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <GrandTotalCell align="right">GRAND TOTAL:</GrandTotalCell>
                <TableCell align="right" sx={{ backgroundColor: "table.totalHighlight", fontSize: 18, fontWeight: 700, padding: "12px 16px" }}>{totalHoursMonthly}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
    );
  }

export default OrganizationTimelogReport;
